import { memo } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { useNavigate } from 'react-router-dom';
import { formatUSD } from '../../utils/formatting';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchResults: Array<Record<string, unknown>>;
  searchLoading: boolean;
}

export const SearchModal = memo(function SearchModal({
  isOpen,
  onClose,
  searchQuery,
  onSearchChange,
  searchResults,
  searchLoading
}: SearchModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold">Search Customers</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by customer name, email, or quote ID..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 h-11"
            />
          </div>

          {searchLoading && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-8 h-8 mb-2 rounded-full bg-accent/10">
                <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
              <p className="text-sm text-muted-foreground">Searching...</p>
            </div>
          )}

          {!searchLoading && searchQuery && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
              </h3>

              {searchResults.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {searchResults.map((quote) => (
                    <SearchResultItem key={quote.id as string} quote={quote} navigate={navigate} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No customers found matching your search</p>
                  <p className="text-sm text-muted-foreground mt-1">Try different keywords or check spelling</p>
                </div>
              )}
            </div>
          )}

          {!searchQuery && (
            <div className="text-center py-8">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Start typing to search customers</p>
              <p className="text-sm text-muted-foreground mt-1">Search by name, email, or quote ID</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

interface SearchResultItemProps {
  quote: Record<string, unknown>;
  navigate: ReturnType<typeof useNavigate>;
}

const SearchResultItem = memo(function SearchResultItem({ quote, navigate }: SearchResultItemProps) {
  const input = (quote?.input || {}) as Record<string, unknown>;
  const customerContact = (quote?.customerContact || {}) as Record<string, unknown>;
  const breakdown = (quote?.breakdown || {}) as Record<string, unknown>;

  const handleCreateSimilar = () => {
    const prefill = {
      boxType: input.boxType || 'tuck-top-snap-lock',
      length: typeof input.length === 'number' ? input.length : 4,
      width: typeof input.width === 'number' ? input.width : 4,
      height: typeof input.height === 'number' ? input.height : 4,
      material: input.material || 'cardboard',
      gsm: typeof input.gsm === 'number' ? input.gsm : 300,
      corrugatedType: input.corrugatedType,
      printing: input.printing || { cmyk: 0, pantone: 0, metallic: 0 },
      insidePrinting: input.insidePrinting || { cmyk: 0, pantone: 0, metallic: 0 },
      quantity: typeof input.quantity === 'number' ? input.quantity : 100,
      features: Array.isArray(input.features) ? input.features : [],
      featureAreas: input.featureAreas || {},
      laminationBothSides: !!input.laminationBothSides,
      vendorProfit: typeof ((quote.vendorProfit as number) ?? (input.vendorProfit as number)) === 'number'
        ? ((quote.vendorProfit as number) ?? (input.vendorProfit as number))
        : 20,
      coatingType: input.coatingType,
      shippingOption: input.shippingOption || 'UPS',
      shippingCountry: input.shippingCountry || 'USA',
    };
    const openDimensions = (breakdown as Record<string, unknown>)?.openDimensions as Record<string, number> | undefined;
    const prefillOpenDimensions = openDimensions ? {
      openLength: openDimensions.openLength,
      openWidth: openDimensions.openWidth,
    } : undefined;
    const prefillInputMode = prefillOpenDimensions ? 'openSize' : 'dimensions';
    navigate('/calculator', {
      state: {
        prefill,
        prefillOpenDimensions,
        prefillInputMode,
        prefillKey: `${quote.id}-${Date.now()}`
      }
    });
  };

  return (
    <div className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium">{(quote.customerName as string) || 'Unknown Customer'}</h4>
        <Badge className="text-xs">
          {formatUSD((quote.totalWithProfit as number) || (quote.totalWithMargin as number) || (breakdown?.total as number) || 0)}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
        <div>
          <span className="font-medium">Email:</span> {(customerContact?.email as string) || 'N/A'}
        </div>
        <div>
          <span className="font-medium">Date:</span> {new Date(quote.createdAt as string).toLocaleDateString()}
        </div>
        <div>
          <span className="font-medium">Box Size:</span> {
            (typeof input?.length === 'number' && typeof input?.width === 'number' && typeof input?.height === 'number')
              ? `${input.length} × ${input.width} × ${input.height} in`
              : 'N/A'
          }
        </div>
        <div>
          <span className="font-medium">Quantity:</span> {(input?.quantity as number) ?? 'N/A'}
        </div>
        <div>
          <span className="font-medium">Box Type:</span> {(input?.boxType as string) || 'N/A'}
        </div>
        <div>
          <span className="font-medium">Status:</span> {(quote.status as string) || 'Active'}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/quotes/${quote.id}`)}
          className="text-xs"
        >
          View Quote
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCreateSimilar}
          className="text-xs"
        >
          Create Similar
        </Button>
      </div>
    </div>
  );
});
